import { Box, Skeleton } from "@mui/material";
import { useTranslation } from "../localization/useTranslation";
import { useUserHook } from "./User.hooks";
import { useUserStyles } from "./User.styles";
import AddEditUserDialog from "./addEditUserDialog/AddEditUserDialog";
import { DeleteUserDialog } from "./deleteUserDialog/DeleteUserDialog";
import { ChangeActiveStatusDialog } from "./changeActiveStatusDialog/ChangeActiveStatusDialog";
import { DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import ResendInvitationDialog from "./resendInvitationDialog/ResendInvitationDialog";
import { getSettings } from "../common/settingsProvider";
import PageHeaderEditEntityButton, { PageHeaderEditEntityButtonMenuItem } from "../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton.tsx";
import { getStartPageName } from "./User.types.ts";
import ReviewUserResponsibility from "./reviewUserResponsibility/ReviewUserResponsibility.tsx";
import UserRoleNames from "./userRoleNames/UserRoleNames.tsx";

function User() {
    const { translateString, translateDate } = useTranslation();
    const userContext = useUserContext();
    const styles = useUserStyles();
    const {
        isLoading,
        user,
        getBreadCrumbs,
        showEditUserDialog,
        setShowEditUserDialog,
        handleEditUserSuccess,
        showDeleteUserDialog,
        setShowDeleteUserDialog,
        handeDeleteUserSuccess,
        handleChangeStatus,
        setShowChangeActiveStatusDialog,
        showChangeActiveStatusDialog,
        showResendInvitationDialog,
        setShowResendInvitationDialog,
        userId,
    } = useUserHook();

    const getIsActive = (isActive?: boolean) => {
        if (isActive) {
            return translateString("active");
        }
        return translateString("deactivated");
    };

    const getEditMenuItems = () => {
        let menuItems: Array<PageHeaderEditEntityButtonMenuItem> = [
            {
                menuItem: translateString("editUser"),
                onClick: () => {
                    setShowEditUserDialog(true);
                },
            },
            {
                menuItem: user?.isActive ? translateString("deactivateUser") : translateString("activateUser"),
                onClick: () => {
                    setShowChangeActiveStatusDialog(true);
                },
            },
            {
                menuItem: translateString("deleteUser"),
                onClick: () => {
                    setShowDeleteUserDialog(true);
                },
            },
            {
                menuItem: translateString("resendActivationEmail"),
                onClick: () => {
                    setShowResendInvitationDialog(true);
                },
            },
        ];

        return menuItems;
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs()}
                rightComponent={!isLoading ? <PageHeaderEditEntityButton menuItems={getEditMenuItems()} /> : <Skeleton variant="text" width={"5%"} />}
            />
            <EntityDetailTopBar>
                <TopBarRow>
                    <TopBarTextTile label={translateString("name")} value={user ? user.firstName + " " + user.lastName : undefined} isLoading={isLoading} />
                    <TopBarTextTile label={translateString("emailAddress")} value={user?.emailAddress} isLoading={isLoading} />
                    <TopBarTextTile label={translateString("role")} value={<UserRoleNames roles={user?.userRoles} />} isLoading={isLoading} />
                    <TopBarTextTile label={translateString("businessAreas")} value={user?.businessAreas.map((x) => x.name).join(", ")} isLoading={isLoading} />
                    <TopBarTextTile
                        label={translateString("groupEntities")}
                        value={user?.gdpo ? translateString("fullGroupAccess") : user?.userGroupEntities.map((x) => x.name).join(", ")}
                        isLoading={isLoading}
                    />
                </TopBarRow>
                <TopBarRow>
                    <TopBarTextTile label={translateString("userStatus")} value={getIsActive(user?.isActive)} isLoading={isLoading} />
                    <TopBarTextTile label={translateString("lastLogin")} value={user?.lastLogin ? translateDate(user.lastLogin) : ""} isLoading={isLoading} />
                    <TopBarTextTile
                        label={translateString("startPage")}
                        value={user?.startPage ? getStartPageName(user!.startPage, translateString) : undefined}
                        isLoading={isLoading}
                    />
                    <TopBarTextTile label={translateString("created")} value={user?.created ? translateDate(user.created) : ""} isLoading={isLoading} />
                    {userContext.permissions.canChangeUserLoginProvider && (
                        <TopBarTextTile
                            label={translateString("idpUser")}
                            value={translateString("editIdpUser")}
                            linkTo={`${getSettings().backofficeUrl}/user-view/${user?.integrationIdentifier}/`}
                            isLoading={isLoading}
                        />
                    )}
                </TopBarRow>
            </EntityDetailTopBar>
            <ReviewUserResponsibility userId={userId} />
            {showEditUserDialog && (
                <AddEditUserDialog isEdit onCloseDialog={() => setShowEditUserDialog(false)} onSaveCorrect={handleEditUserSuccess} userId={user!.id} />
            )}
            {showDeleteUserDialog && (
                <DeleteUserDialog
                    userId={user!.id}
                    onCloseDialog={() => setShowDeleteUserDialog(false)}
                    onSaveCorrect={() => handeDeleteUserSuccess(user!.firstName + " " + user!.lastName)}
                    userName={user!.firstName + " " + user!.lastName}
                />
            )}
            {showChangeActiveStatusDialog && (
                <ChangeActiveStatusDialog
                    userId={user!.id}
                    onCloseDialog={() => setShowChangeActiveStatusDialog(false)}
                    onSaveCorrect={() => handleChangeStatus()}
                    userName={user!.firstName + " " + user!.lastName}
                    activeStatus={user!.isActive}
                />
            )}
            {showResendInvitationDialog && <ResendInvitationDialog userId={user!.id} onCloseDialog={() => setShowResendInvitationDialog(false)} />}
        </Box>
    );
}

export default User;
